'use client';

import { ipcLog } from '@/features/games-app/shared/client/ipc-client-logger';
import {
  IPCEventListener,
  IPCMessage,
  IPCMessageKey,
  IPCRequest,
} from '@/features/games-app/shared/types/ipc.types'; // where your interfaces/enums are defined

export class IPCClient {
  private _pending = new Map<
    string,
    {
      resolve: (value: unknown) => void;
      reject: (reason?: unknown) => void;
      timeoutId: ReturnType<typeof setTimeout>;
    }
  >();

  private _eventListeners = new Map<IPCMessageKey, Set<IPCEventListener>>();
  private _defaultTimeout: number;

  constructor({ defaultTimeout = 10000 }: { defaultTimeout?: number } = {}) {
    this._defaultTimeout = defaultTimeout;
    this._initReceive();
  }

  private _initReceive() {
    if (!window?.wildtangent?.IPCBeginReceive) {
      return;
    }

    window.wildtangent.IPCBeginReceive((json: string) => {
      let parsed: IPCMessage;

      try {
        parsed = JSON.parse(json);
        console.log('IPC Received:', parsed);
      } catch (e) {
        ipcLog.error('Failed to parse IPC message', e);
        return;
      }

      const { id, name, action, body } = parsed;
      const key = `${name}:${action}` as IPCMessageKey;

      if (!id) {
        const listeners = this._eventListeners.get(key);
        if (listeners) {
          for (const fn of listeners) {
            try {
              fn(body, name, action);
            } catch (e) {
              ipcLog.error(`Listener error for event ${key}`, e);
            }
          }
        } else {
          ipcLog.warn(`No listeners registered for unsolicited event: ${key}`);
        }
        return;
      }

      const pending = this._pending.get(id);
      if (pending) {
        clearTimeout(pending.timeoutId);
        this._pending.delete(id);
        console.log(`IPC Response matched for ID: ${id}`, body);
        pending.resolve(body);
      } else {
        ipcLog.warn(`No pending request for ID: ${id}`);
      }
    });
  }

  public async send<T>(request: IPCRequest, timeout = this._defaultTimeout): Promise<T> {
    const id = `ipc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const key = `${request.name}:${request.action}` as IPCMessageKey;
    const message: IPCMessage = { ...request, id };

    return new Promise<T>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this._pending.delete(id);
        ipcLog.error(`IPC request timed out: ${key} (id: ${id})`);
        reject(new Error(`Timeout after ${timeout}ms for ${key}`));
      }, timeout);

      this._pending.set(id, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timeoutId,
      });

      try {
        window.wildtangent?.IPCSendMessage?.(JSON.stringify(message));
      } catch (e) {
        clearTimeout(timeoutId);
        this._pending.delete(id);
        ipcLog.error(`IPC send failed for ${key}`, e);
        reject(e);
      }
    });
  }

  public on(key: IPCMessageKey, fn: IPCEventListener) {
    if (!this._eventListeners.has(key)) {
      this._eventListeners.set(key, new Set());
    }
    this._eventListeners.get(key)!.add(fn);
  }

  public off(key: IPCMessageKey, fn: IPCEventListener) {
    this._eventListeners.get(key)?.delete(fn);
  }
}

let _client: IPCClient | null = null;

export const getIpcClient = ({
  defaultTimeout = 10000,
}: { defaultTimeout?: number } = {}): IPCClient => {
  if (typeof window === 'undefined') {
    throw new Error('ipcClient can only be used in the browser');
  }

  if (!_client) {
    _client = new IPCClient({ defaultTimeout });
  }

  return _client;
};
